import React, {useState, useLayoutEffect, useContext, useEffect} from 'react';
import styled from 'styled-components';

import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Pagination from '@mui/material/Pagination';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {QueryContext, useAuthRedirects} from '../App';
import {useLocation} from 'react-router-dom';
import {useDebouncedEffect} from '../lib/useDebouncedEffect';
import {$get} from '../lib/requests';
import {BookCard} from './BookCard';
import {CollectionCard} from './CollectionCard';

import {Book} from 'shared';

interface CollectionItem {
  name?: string;
  year?: number;
  bookCount?: number;
}

type MainAreaItem = Book | CollectionItem;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  width: 100%;
  height: 64px;
  padding-left: 16px;
  padding-right: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px rgba(32, 33, 36, 0.1) solid;
`;

const MainContainer = styled.div`
  flex-grow: 1;
  max-height: 100%;
  overflow: auto;
  height: calc(100vh - 130px);
  padding: 16px;
`;

const ItemsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const ValignContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const OrderToggle = styled.button`
  cursor: pointer;
  width: 100px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 4px 8px;
  background: transparent;
  font-size: 16px;

  border-radius: 4px;
  border: 1px rgba(32, 33, 36, 0.3) solid;
`;

const Label = styled.span`
  font-size: 16px;
`;

const SORT_BY_FIELDS: Record<string, string[]> = {
  books: ['ନାମ', 'ଲେଖକ', 'ବିଷୟ', 'ବର୍ଷ'],
  authors: ['ବହି ସଂଖ୍ୟା', 'ନାମ'],
  categories: ['ବହି ସଂଖ୍ୟା', 'ନାମ'],
  years: ['ବହି ସଂଖ୍ୟା', 'ବର୍ଷ'],
};

const PAGE_TYPE: Record<string, string> = {
  ଲେଖକ: 'authors',
  ବିଷୟ: 'categories',
  ବର୍ଷ: 'years',
};

export default function MainArea({assignedTo}: {assignedTo?: string}) {
  const authed = useAuthRedirects();
  const {search} = useLocation();
  // eslint-disable-next-line n/no-unsupported-features/node-builtins
  const searchValue = new URLSearchParams(search).get('q');

  const [pageType, setPageType] = useState<string>(
    PAGE_TYPE[String(searchValue)] || 'books'
  );
  const [cardType, setCardType] = useState<string>(
    pageType === 'books' ? 'book' : 'collection'
  );
  const [items, setItems] = useState<MainAreaItem[]>([]);
  const [curPage, setCurPage] = useState<number>(1);
  const [sortOrder, setSortOrder] = useState({
    by: SORT_BY_FIELDS[pageType][0],
    order: SORT_BY_FIELDS[pageType][0] === 'ବହି ସଂଖ୍ୟା' ? 'desc' : 'asc',
  });
  const [count, setCount] = useState<number>(0);
  const {searchString, fixedQuery, setFixedQuery, setSearchString} =
    useContext(QueryContext);

  async function getBooks() {
    const res = await $get<{books: Book[]; numBooks: number}>(
      `/api/books?page=${curPage}&sortBy=${sortOrder.by}&sortOrder=${
        sortOrder.order
      }${fixedQuery ? `&fq=${fixedQuery}` : ''}&q=${searchString || ''}${assignedTo ? `&assignedTo=${assignedTo}` : ''}`
    );
    if (res) {
      setItems(res.books || []);
      setCount(res.numBooks || 0);
    }
    setCardType('book');
  }

  async function getAuthors() {
    const res = await $get<{authors: CollectionItem[]; numAuthors: number}>(
      `/api/authors?page=${curPage}&sortBy=${sortOrder.by}&sortOrder=${
        sortOrder.order
      }&q=${searchString || ''}`
    );
    if (res) {
      setItems(res.authors || []);
      setCount(res.numAuthors || 0);
    }
    setCardType('collection');
  }

  async function getCategories() {
    const res = await $get<{
      categories: CollectionItem[];
      numCategories: number;
    }>(
      `/api/categories?page=${curPage}&sortBy=${sortOrder.by}&sortOrder=${
        sortOrder.order
      }&q=${searchString || ''}`
    );
    if (res) {
      setItems(res.categories || []);
      setCount(res.numCategories || 0);
    }
    setCardType('collection');
  }

  async function getYears() {
    const res = await $get<{years: CollectionItem[]; numYears: number}>(
      `/api/years?page=${curPage}&sortBy=${sortOrder.by}&sortOrder=${
        sortOrder.order
      }&q=${searchString || ''}`
    );
    if (res) {
      setItems(res.years || []);
      setCount(res.numYears || 0);
    }
    setCardType('collection');
  }

  async function getData() {
    if (pageType === 'authors') {
      await getAuthors();
    } else if (pageType === 'categories') {
      await getCategories();
    } else if (pageType === 'years') {
      await getYears();
    } else {
      await getBooks();
    }
  }

  function onPageChange(_e: React.ChangeEvent<unknown>, pageNo: number) {
    setCurPage(pageNo);
  }

  function onSortByChange(e: {target: {value: unknown}}) {
    const val = String(e.target.value);
    setSortOrder({
      by: val,
      order: val === 'ବହି ସଂଖ୍ୟା' ? 'desc' : 'asc',
    });
  }

  function toggleSortOrder() {
    setSortOrder({
      by: sortOrder.by,
      order: sortOrder.order === 'asc' ? 'desc' : 'asc',
    });
  }

  useDebouncedEffect(
    () => {
      getData();
    },
    [pageType, fixedQuery, searchString, curPage, sortOrder, assignedTo],
    100
  );

  useLayoutEffect(() => {
    setCurPage(1);
  }, [searchString]);

  useEffect(() => {
    setFixedQuery(searchValue);
    setSearchString('');
    setCurPage(1);
    setPageType(PAGE_TYPE[String(searchValue)] || 'books');
  }, [searchValue, setFixedQuery, setSearchString]);

  useEffect(() => {
    setSortOrder({
      by: SORT_BY_FIELDS[pageType][0],
      order: SORT_BY_FIELDS[pageType][0] === 'ବହି ସଂଖ୍ୟା' ? 'desc' : 'asc',
    });
  }, [pageType]);

  const pageSize = pageType === 'books' ? 50 : 100;
  const startIndex = (curPage - 1) * pageSize + 1;
  const endIndex = Math.min(curPage * pageSize, count);
  const numPages = Math.ceil(count / pageSize);

  if (!authed) return null;

  return (
    <Container>
      <HeaderContainer>
        <ValignContainer>
          <Label>ଶୃଙ୍ଖଳା</Label>
          <FormControl size="small">
            <Select
              value={sortOrder.by}
              onChange={onSortByChange}
              variant="outlined"
            >
              {SORT_BY_FIELDS[pageType].map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <OrderToggle onClick={toggleSortOrder}>
            {sortOrder.order === 'asc' ? (
              <>
                <ArrowUpwardIcon fontSize="small" />
                <span>ଉର୍ଦ୍ଧ୍ଵ କ୍ରମ</span>
              </>
            ) : (
              <>
                <ArrowDownwardIcon fontSize="small" />
                <span>ଅଧଃ କ୍ରମ</span>
              </>
            )}
          </OrderToggle>
        </ValignContainer>
        <ValignContainer>
          <Label>
            {startIndex}-{endIndex}, ସମୁଦାୟ {count}
          </Label>
          <Pagination
            count={numPages}
            shape="rounded"
            onChange={onPageChange}
            page={curPage}
          />
        </ValignContainer>
      </HeaderContainer>
      <MainContainer>
        <ItemsGrid>
          {items.map((item, index) => {
            if (cardType === 'book' && 'bookid' in item) {
              return <BookCard key={item.bookid} book={item} />;
            }
            if (!('bookid' in item)) {
              const collectionKey =
                item.name || (item.year !== undefined ? item.year : index);
              return (
                <CollectionCard
                  key={collectionKey}
                  item={item}
                  type={pageType}
                />
              );
            }
            return null;
          })}
        </ItemsGrid>
      </MainContainer>
    </Container>
  );
}
